/* eslint-disable eqeqeq */
import { RadioGroup, RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import { AxiosResponse } from 'axios';
import { uniqBy } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import multiCompanyTemplate from 'src/assets/img/import_template_multi_company.xls';
import singleCompanyTemplate from 'src/assets/img/import_template_single_company.xls';
import DarkBackgroundLoadingPanel from 'src/components/DarkBackgroundLoadingPanel';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import { CanImportMultipleCompanies } from 'src/components/guards/PermissionsList';
import GuideMeTitleBar from 'src/components/GuideMeTitleBar';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import usePermission from 'src/hooks/usePermissions';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useAllGetFiscalYearsApi from 'src/services/useAllGetFiscalYearsApi';
import { BudgetImportBody } from 'src/services/useImportBudgetApi';
import { S3UploadResponse } from 'src/services/useInvoiceS3UploadApi';
import useLastImportDetailsApi from 'src/services/useLastImportDetailsApi';
import { SetState } from 'src/types/generic';
import { SUPER_ADMIN_ID } from 'src/utils/constants';
import { downloadFile } from 'src/utils/downloadFile';
import { formatFiscalYears } from 'src/utils/transformation';
import { fiscalYearType } from './Export';
import FiscalYearDropdown from './FiscalYearDropdown';
import Results from './Results';
import UploadImportFile from './UploadImportFile';

const amountTypeData = [
  { label: 'Import Budget Amounts', value: 'budget' },
  { label: 'Import Forecast Amounts', value: 'forecast' },
];
const importTypeData = [
  { label: 'Multi-Property', value: 'multi_property' },
  { label: 'Single Property', value: 'single_property' },
];

export const useFileProps = (file: File | null) => {
  const isTypeAcceptable = file?.name
    ? ['xls', 'xlsx', 'txt'].includes(file.name.split('.').pop()?.toLowerCase() || '')
    : false;
  const errors: string[] = [];
  const info: string[] = [];
  const allowableSize = 12 * 1024 * 1024;
  const isSizeAcceptable = file && file.size < allowableSize;
  if (!file) errors.push('Select a File to Import.');
  if (file && !isTypeAcceptable) errors.push('Uploaded file must be in acceptable format');
  if (file && !isSizeAcceptable) errors.push('File should be less than 12 MB');
  if (file && isTypeAcceptable && isSizeAcceptable)
    info.push('Your upload is accepted. Please continue to import.');
  return { isAcceptable: isTypeAcceptable && isSizeAcceptable, errors, info };
};

const userInstruction = () => (
  <ul className="px-4 text-[13px] [&>li]:list-disc [&>li]:font-semibold [&>li]:mb-2">
    <li>
      1. All data must be in the correct cells - do not move columns or cell values around or add
      any new ones.
    </li>
    <li>
      2. Budget/forecast amounts must be formatted as NUMBER with up to 2 decimal places (no commas,
      dashes, $, etc.).
    </li>
    <li>3. Spreadsheet tab positions must not be altered.</li>
    <li>
      4. No additional text should appear anywhere (on any of the tabs) that is outside the
      mandatory cells.
    </li>
    <li>5. At minimum, the GL Codes and descriptions must be populated.</li>
    <li>
      6. Active column must be either (not case sensitive): 1, Active, A, Yes, Y OR 0, Inactive, I,
      No, N OR 2, D, Delete
    </li>
    <li>7. Fixed column must be either (not case sensitive): 1, Yes, Y OR 0, No, N.</li>
    <li>8. DO NOT USE column must not be deleted and must be a 0 or N.</li>
    <li>
      9. GL Template filename is recommended to be as follows (note: budgets/forecast will be either
      or depending on what type you plan to import): <div />
      MgmtCompanyName_PropertyName_FiscalYearStartMonthYear_FiscalYearEndMonthYear_Budgets//Forecasts.xls{' '}
      <div />
      (i.e.: SUNSTONE_COURTYARD_LAX_JANUARY2025_DECEMBER2025_BUDGETS.xls).
    </li>
  </ul>
);
const importModalContent = () => (
  <div className="[&>p]:text-sm  [&>p]:mb-2">
    <p className="font-bold text-red-600">Import process can not be reverted!</p>
    <p>
      Importing checkbook data can not be undone. Please double check the fiscal year and uploaded
      file contains correct GL Codes, forecast amounts, departments, etc.
    </p>
    <p>
      Note: if you are updating budget amounts, previously set period amounts will be overwritten
      with import.
    </p>
    <p>Please confirm you would like to proceed with this import.</p>
  </div>
);

interface importProps {
  handleClose: () => void;
  file: File | null;
  setFile: SetState<File | null>;
  fiscalYear: fiscalYearType | null;
  setFiscalYear: SetState<fiscalYearType | null>;
  amountType: 'budget' | 'forecast';
  setAmountType: SetState<'budget' | 'forecast'>;
  execute: (
    params?:
      | {
          name: string;
        }
      | undefined
  ) => Promise<S3UploadResponse | any>;
  urlExecute: (
    params?:
      | {
          url: string;
          file: File;
        }
      | undefined
  ) => Promise<AxiosResponse<any, any>>;
  importExecute: (params?: BudgetImportBody | undefined) => Promise<AxiosResponse<any, any>>;
  importLoading: boolean;
  s3Loading: boolean;
  fileUploadLoading: boolean;
}

const Import: FC<importProps> = ({
  handleClose,
  amountType,
  file,
  fiscalYear,
  setAmountType,
  setFile,
  setFiscalYear,
  execute,
  importExecute,
  urlExecute,
  s3Loading,
  importLoading,
  fileUploadLoading,
}) => {
  const { user } = useAuth();
  const { company } = useCompany();
  const { t } = usePeriodView();
  const { fiscalYears, getFiscalYears, isLoading } = useAllGetFiscalYearsApi(
    +company?.companyId,
    false
  );
  const { hasUsesInitialBudgetsPermission } = useViewCheckbook();
  const { confirm } = useConfirm();

  const [importType, setImportType] = useState<'single_property' | 'multi_property'>(
    'single_property'
  );
  const isSuperAdmin = company.companyId == SUPER_ADMIN_ID;

  useEffect(() => {
    getFiscalYears();
  }, [getFiscalYears]);

  const fiscalYearDropdownData = useMemo(
    () => uniqBy(formatFiscalYears(fiscalYears.fiscalYears), 'content'),
    [fiscalYears]
  );

  const handleAmountTypeChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    setAmountType(value);
  };
  const handleImportTypeChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    setImportType(value);
    setFiscalYear(null);
  };

  const isMultipleCompaniesImportable = usePermission({
    permissions: CanImportMultipleCompanies,
    or: true,
  });

  const canHaveMultiImport = useMemo(
    () => isSuperAdmin || (fiscalYears.hasChildCompanies && isMultipleCompaniesImportable),
    [fiscalYears.hasChildCompanies, isMultipleCompaniesImportable, isSuperAdmin]
  );

  useEffect(() => {
    if (canHaveMultiImport) {
      setImportType('multi_property');
    }
  }, [canHaveMultiImport]);

  const handleDownloadTemplate = () => {
    const fileUrl =
      isSuperAdmin || (fiscalYears.hasChildCompanies && isMultipleCompaniesImportable)
        ? importType === 'multi_property'
          ? multiCompanyTemplate
          : singleCompanyTemplate
        : singleCompanyTemplate;
    const fileName =
      isSuperAdmin || (fiscalYears.hasChildCompanies && isMultipleCompaniesImportable)
        ? importType === 'multi_property'
          ? 'import_template_multi_company.xls'
          : 'import_template_single_company.xls'
        : 'import_template_single_company.xls';
    downloadFile(fileUrl, fileName);
  };

  const handleViewInstruction = async () => {
    await confirm({
      title: <GuideMeTitleBar title="INSTRUCTIONS FOR USE OF GL_BUDGETS TEMPLATE" />,
      content: userInstruction(),
      cancelButtonText: 'Close',
      hasNoConfirmButton: true,
      width: '780px',
      id: 'Instruction',
      className: 'instructions_popup',
    });
  };

  const { refreshGLCodePeriodTable } = useViewCheckbook();
  const { value: lastImportDetails, execute: fetchLastDetails } = useLastImportDetailsApi(
    company.companyId
  );

  const isFiscalYearVisible = useMemo(() => {
    if (importType === 'single_property') return true;
    if (isSuperAdmin) return false;

    if (fiscalYears.hasChildCompanies) {
      if (!isMultipleCompaniesImportable) {
        return true;
      }
      return false;
    } else return true;
  }, [fiscalYears.hasChildCompanies, importType, isMultipleCompaniesImportable, isSuperAdmin]);
  console.log(importType);

  const handleImport = async () => {
    const confirmStatus = await confirm({
      title: 'WARNING',
      content: importModalContent(),
      confirmButtonText: 'Proceed and Import',
      cancelButtonText: 'Cancel',
      width: '500px',
    });

    if (confirmStatus && file) {
      const { data } = await execute({ name: file.name });
      const { status } = await urlExecute({ url: data.signedUrl, file });
      if (status !== 200) {
        return;
      }

      const importBudget = {
        fileName: data?.fileNameWithAttachId,
        originalFileName: file?.name,
        companyId: +company.companyId,
        startDate: (fiscalYear?.startDate as string) ?? null,
        endDate: (fiscalYear?.endDate as string) ?? null,
        importType: amountType === 'budget' ? 0 : 1,
        multiCompanyImport: importType === 'multi_property',
        acctGlBudgetImportId: data.attachId,
        loggedInClientId: user.ClientId,
      };
      const { status: apiStatus } = await importExecute(importBudget);
      if (apiStatus === 202) {
        toast.success('Import request submitted successfully.');
        setFile(null);
        setFiscalYear(null);
        setAmountType('forecast');
        fetchLastDetails();
        refreshGLCodePeriodTable();
      }
    }
  };

  const { isAcceptable, errors: fileErrors } = useFileProps(file);

  const { isSaveDisabled, errors } = useMemo(() => {
    let errors = [];
    const isSaveDisabled = !isAcceptable || (isFiscalYearVisible && !fiscalYear);
    if (isFiscalYearVisible && !fiscalYear) {
      errors.push('Select a Fiscal Year to Import.');
    }
    errors = [...errors, ...fileErrors];
    return { errors, isSaveDisabled };
  }, [fileErrors, fiscalYear, isAcceptable, isFiscalYearVisible]);

  return (
    <div className="p-[10px] bg-[#ffffff]">
      <div className="text-xs mb-4">
        <p className="mb-2 mt-2">
          {t(
            'Here you can import GL Code info (description, active/fixed status, GL Department assignment) and period amounts for the Checkbook for a selected Fiscal Year. Existing codes in the import will be updated, and new codes will be created.'
          )}
        </p>
        <p>
          {t(
            'Note: if your company uses budgets, you will need to select if you are updating budget OR forecast values in the import file'
          )}
        </p>
      </div>
      {canHaveMultiImport ? (
        <div className="mb-2">
          <label className="font-bold text-xs">{t('Import Type')}</label>
          <RadioGroup
            data={importTypeData}
            layout={'horizontal'}
            className="text-xs"
            onChange={handleImportTypeChange}
            value={importType}
          />
        </div>
      ) : (
        ''
      )}
      <div className="flex justify-between items-center  p-2 mb-2">
        <div>
          <p className="font-bold text-xs mb-1">{t('Template File')}</p>
          <p className="text-xs text-link cursor-pointer underline" onClick={handleViewInstruction}>
            {t('View Instructions')}
          </p>
        </div>
        <p className="text-xs text-link cursor-pointer underline" onClick={handleDownloadTemplate}>
          {t('Download')}
        </p>
      </div>

      {!isLoading &&
        (isFiscalYearVisible ? (
          <div className="mb-2">
            <FiscalYearDropdown
              fiscalYear={fiscalYear}
              setFiscalYear={setFiscalYear}
              data={fiscalYearDropdownData}
            />
          </div>
        ) : (
          <p className="text-sm mb-2">You must dictate Fiscal Year in imported file</p>
        ))}
      {hasUsesInitialBudgetsPermission && (
        <div className="mb-2">
          <label className="font-bold text-xs">{t('Import Amounts')}</label>
          <RadioGroup
            data={amountTypeData}
            layout={'horizontal'}
            className="text-xs"
            onChange={handleAmountTypeChange}
            value={amountType}
          />
        </div>
      )}

      <UploadImportFile file={file} setFile={setFile} />
      <div className="flex items-center justify-end">
        <PopUp
          show={isSaveDisabled}
          content={<PopUpContentWrapper type="Error" contents={errors} />}
        >
          <Button
            size={'md'}
            color={'info'}
            onClick={handleImport}
            disabled={isSaveDisabled}
            loading={s3Loading || importLoading || fileUploadLoading}
          >
            {t('Import')}
          </Button>
        </PopUp>
        <Button size="md" color="secondary" onClick={handleClose}>
          {t('Cancel')}
        </Button>
      </div>
      <Results lastImportDetails={lastImportDetails} />
      {isLoading && <DarkBackgroundLoadingPanel id="ImportPanel" />}
    </div>
  );
};

export default Import;
